import React, { useCallback } from "react";
import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { StoreFilesScreenComponent } from "../../../../Files/FilesScreenComponent";
import { StoreAllFilesComponent } from "../../../../Files/AllFilesComponent";

import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorParamList,
  UserLoggedInNavigatorRouteProp,
} from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";

import { HeaderButton } from "../../../../../components";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { onlyUnique } from "../../../../../utils";
import { WSFileMain } from "./WSFile";
import { WorkspaceFilesScreenComponent } from "../../../../OrgHome/WorkspaceFiles/WorkspaceFiles";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { AHIcon, useTheme } from "arkh";

const Stack = createNativeStackNavigator<ParamList>();
type ParamList = {
  WSFileMain: {
    fileIds: string[] | null;
    slideshowDuration: number | null;
  } & UserLoggedInNavigatorParamList["WSFile"];
  WSWorkspaceFilesSelect: {
    fileIds: string[];
    slideshowDuration: number | null;
    workspaceId: string;
  } & UserLoggedInNavigatorParamList["WSFile"];
  WSFileSelect: {
    fileIds: string[] | null;
    slideshowDuration: number | null;
  } & UserLoggedInNavigatorParamList["WSFile"];
  WSAllFileSelect: {
    fileIds: string[] | null;
    slideshowDuration: number | null;
  } & UserLoggedInNavigatorParamList["WSFile"];
};
export type WSFileRouteProp<T extends keyof ParamList> = RouteProp<
  ParamList,
  T
>;

export type WSFileNavigationProp<T extends keyof ParamList> =
  CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, T>,
    NativeStackNavigationProp<UserLoggedInNavigatorParamList>
  >;

function WSFileSelect({
  navigation,
  route: { params },
}: {
  navigation: WSFileNavigationProp<"WSFileSelect">;
  route: WSFileRouteProp<"WSFileSelect">;
}) {
  return (
    <StoreFilesScreenComponent
      includeIntegrationFiles={false}
      onPressFile={async ({ fileId, fileType, fileName, url }) =>
        navigation.navigateDeprecated("WSFileMain", {
          ...params,
          isOrg: false,
          fileIds: [...(params.fileIds ?? []), fileId].filter(onlyUnique),
        })
      }
      onUploadedFiles={(files) => {
        navigation.navigateDeprecated("WSFileMain", {
          ...params,
          isOrg: false,
          fileIds: [...(params.fileIds ?? []), ...files].filter(onlyUnique),
        });
      }}
      setHeaderRight={(p) => {
        navigation.setOptions({
          headerRight: p,
        });
      }}
      modal
      onShowAllFiles={() => {
        navigation.navigateDeprecated("WSAllFileSelect", params);
      }}
    />
  );
}

function WSAllFileSelect({
  navigation,
  route: { params },
}: {
  navigation: WSFileNavigationProp<"WSAllFileSelect">;
  route: WSFileRouteProp<"WSAllFileSelect">;
}) {
  return (
    <StoreAllFilesComponent
      includeIntegrationFiles={false}
      onPressFile={async ({ fileId, fileType, fileName, url }) => {
        navigation.navigateDeprecated("WSFileMain", {
          ...params,
          isOrg: false,
          fileIds: [...(params.fileIds ?? []), fileId].filter(onlyUnique),
        });
      }}
    />
  );
}

export const WSFileStack = withWebModal(
  (props: {
    navigation: UserLoggedInNavigatorNavigationProp<"WSFile">;
    route: UserLoggedInNavigatorRouteProp<"WSFile">;
  }) => {
    const theme = useTheme();
    const intl_tokens = useIntlTokens();
    return (
      <Stack.Navigator
        screenOptions={{
          headerShown: true,
        }}
      >
        <Stack.Screen
          name="WSFileMain"
          component={WSFileMain}
          options={{
            title: intl_tokens.Files,
            headerLeft: () => (
              <HeaderButton type="BACK" onPress={props.navigation.goBack} />
            ),
          }}
          initialParams={{
            ...props.route.params,
          }}
        />
        <Stack.Screen
          name="WSWorkspaceFilesSelect"
          component={WorkspaceFilesSelect}
          options={{
            title: "",
            headerShadowVisible: false,
            contentStyle: {
              backgroundColor: theme["surface-background-light"],
            },
          }}
          initialParams={{ ...props.route.params }}
        />
        <Stack.Screen
          name="WSFileSelect"
          component={WSFileSelect}
          options={{
            title: intl_tokens.Files,
          }}
          initialParams={{ ...props.route.params }}
        />
        <Stack.Screen
          name="WSAllFileSelect"
          component={WSAllFileSelect}
          options={{
            title: intl_tokens.Files,
          }}
          initialParams={{ ...props.route.params }}
        />
      </Stack.Navigator>
    );
  },
  false
);

function WorkspaceFilesSelect(props: {
  navigation: WSFileNavigationProp<"WSWorkspaceFilesSelect">;
  route: WSFileRouteProp<"WSWorkspaceFilesSelect">;
}) {
  const onDone = useCallback(
    (fileIds: string[]) => {
      props.navigation.navigateDeprecated("WSFileMain", {
        fileIds: [...props.route.params.fileIds, ...fileIds].filter(onlyUnique),
        slideshowDuration: props.route.params.slideshowDuration,
        isOrg: true,
        unitSize: props.route.params.unitSize,
        widgetId: props.route.params.widgetId,
        workspaceId: props.route.params.workspaceId,
      });
    },
    [
      props.navigation,
      props.route.params.fileIds,
      props.route.params.slideshowDuration,
      props.route.params.unitSize,
      props.route.params.widgetId,
      props.route.params.workspaceId,
    ]
  );

  const onPress = useCallback(
    (file: { id: string }) => onDone([file.id]),
    [onDone]
  );

  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <WorkspaceFilesScreenComponent
          color="surface-background-light"
          fetchKey={fetchKey}
          onFileItemPress={onPress}
          itemActionComponent={<AHIcon type="add" color="primary" />}
          onUploadedFiles={onDone}
          workspaceId={props.route.params.workspaceId}
          write
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
